import { useQuery } from '@tanstack/react-query';
import config from '../config';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import { useUserAssetsStore } from '../hooks/useUserAssetsStore';
import formatNumber from '../utils/formatNumber';
import { getKittyName } from '../utils/kittyFormatting';
import Alert from './Alert';
import Spinner from './Spinner';
import { StatCard } from './StatCard';
import Steps from './Steps';
import TraitShuffleFloatingActions from './TraitShuffleFloatingActions';
import TraitShufflePreview from './TraitShufflePreview';
import TraitShuffleSelect from './TraitShuffleSelect';
import TraitShuffleSuccessModal from './TraitShuffleSuccessModal';
import useTraitShuffleStore from '../hooks/useTraitShuffleStore';

const TraitShuffle = () => {
  const { address } = useAlgo();
  const { activeTab, setActiveTab, selectedNft, showSuccessModal, setShowSuccessModal, newKittyMetadata, reset } = useTraitShuffleStore();
  const { myKitties, myKittiesLoading } = useKittiesStore();
  const { isLoading } = useUserAssetsStore();

  const myKittiesForShuffle = myKitties
    ?.filter((kitty) => kitty.gen === 4)
    .filter((kitty) => kitty.properties.Special === 'None')
    .filter((kitty) => {
      const bannedTraits = config.bannedTraits;
      const traitValues = Object.values(kitty.properties);
      return !bannedTraits.some((trait) => traitValues.includes(trait));
    });

  const { data: traitShuffleStats } = useQuery({
    queryKey: ['traitShuffleStats'],
    queryFn: () => fetch('/api/trait-shuffle/stats').then((res) => res.json()),
  });
  const totalTraitShuffles = traitShuffleStats?.total || 0;

  const { data: myTraitShuffleStats } = useQuery({
    queryKey: ['myTraitShuffleStats', address],
    queryFn: () => fetch(`/api/trait-shuffle/stats?address=${address}`).then((res) => res.json()),
  });
  const myTotalTraitShuffles = myTraitShuffleStats?.total || 0;

  const steps = [
    { id: 0, name: 'Select a Shitty Kitty', onClick: () => setActiveTab(0) },
    {
      id: 1,
      name: 'Select trait to shuffle',
      onClick: () => {
        if (selectedNft === null && activeTab === 0) {
          return;
        }
        setActiveTab(1);
      },
    },
  ];

  if (process.env.NEXT_PUBLIC_TRAIT_SHUFFLE_IS_ENABLED !== 'true') {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='info' className={'flex text-center justify-center'}>
          Coming soon...
        </Alert>
      </div>
    );
  }

  if (myKittiesLoading || isLoading) {
    return (
      <div className='flex justify-center mt-20'>
        <Spinner />
      </div>
    );
  }

  if (myKittiesForShuffle.length === 0) {
    return (
      <div className='max-w-lg mx-auto mt-10 text-center'>
        <Alert type='danger' className={'flex text-center justify-center'}>
          You must own a Fourth Litter Shitty Kitty to use the Trait Shuffle.<br></br> Check out the{' '}
          <a href={`/marketplace?collection=Fourth Litter`} className='font-bold'>
            Marketplace
          </a>{' '}
          to purchase a Shitty Kitty.
        </Alert>
      </div>
    );
  }

  const handleModalClose = () => {
    setShowSuccessModal(false);
    reset();
    location.reload();
  };

  const kittyName = selectedNft ? getKittyName(selectedNft) : '';

  return (
    <div className='relative pb-36 mt-8'>
      <div className='flex flex-col md:flex-row mb-6 gap-4 justify-end items-stretch'>
        <div className='flex flex-col md:flex-row gap-4'>
          <div className='min-w-[150px]'>
            <StatCard title='My Shuffles' value={formatNumber(myTotalTraitShuffles)} className='h-full flex flex-col justify-center' />
          </div>
          <div className='min-w-[150px]'>
            <StatCard title='Total Shuffles' value={formatNumber(totalTraitShuffles)} className='h-full flex flex-col justify-center' />
          </div>
        </div>
      </div>
      <div className='mb-6'>
        <Steps steps={steps} activeStep={activeTab} />
      </div>
      {activeTab === 0 && <TraitShuffleSelect kitties={myKittiesForShuffle} />}
      {activeTab === 1 && (
        <>
          <TraitShufflePreview />
        </>
      )}

      <TraitShuffleFloatingActions />

      <TraitShuffleSuccessModal open={showSuccessModal} onClose={handleModalClose} kittyMetadata={newKittyMetadata} kittyName={kittyName} />
    </div>
  );
};

export default TraitShuffle;
